package meineglasfaser.frontend.admin.pages.survey

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.role
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import meineglasfaser.api.Survey
import meineglasfaser.api.SurveyEntry
import meineglasfaser.api.YaTableOptions
import meineglasfaser.api.routes.AdminRoutes
import meineglasfaser.api.routes.PrefetchRoutes
import meineglasfaser.api.routes.PrefetchedEntry
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedDesc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*

@Page("entries/{surveyId?}")
@Composable
fun SurveyEntriesPage(ctx: PageContext) {
    val surveys = prefetch(PrefetchRoutes.Surveys())
    val customers = prefetch(PrefetchRoutes.Customers())

    val surveyId = ctx.route.params["surveyId"]?.ifBlank { null }
    var version by remember { mutableIntStateOf(0) }

    val survey = surveyId?.let {
        rememberQuery<Survey?>(null, version, surveyId) {
            BackendApi.get(AdminRoutes.Survey.Id(surveyId)) {
                withAuth()
            }
        }
    }
    var showUpdateModal by remember { mutableStateOf<Survey?>(null) }
    var showEntryUpdateModal by remember { mutableStateOf<SurveyEntry?>(null) }

    YaTable(
        header = {
            H1 { Text(when (surveyId) {
                null -> "Alle Erhebungseinträge"
                else -> "Erhebungseinträge für ${survey?.title ?: surveyId}"
            }) }
        },
        subheader = {
            if (survey != null) {
                Article {
                    Header { Text(survey.title) }
                    Div {
                        Span { Text("Haushalte: ") }
                        Span { Text("${survey.numAssignedEntries} / ${survey.households}") }
                    }
                    Button(attrs = {
                        role("button")
                        classes("secondary", "small")
                        onClick { showUpdateModal = survey }
                    }) {
                        Text("Erhebung bearbeiten")
                    }
                }
            } else if (surveyId != null) {
                Span({ ariaBusy() }) { Text("Erhebung wird geladen...") }
            }
        },
        idProperty = SurveyEntry::id,
        columns = listOfNotNull(
            if (surveyId == null) SurveyEntry::surveyId named "Erhebung" else null,
            SurveyEntry::name named "Name",
            SurveyEntry::installationAddress named "Installationsadresse",
            SurveyEntry::email named "E-Mail",
            SurveyEntry::phone named "Telefon",
            SurveyEntry::installationType named "Anschlussart",
            SurveyEntry::installationOption named "Anschlussoption",
            SurveyEntry::installationCount named "Anzahl Anschlüsse",
            SurveyEntry::createdAt named "Eingetragen am",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(SurveyEntry::createdAt.sortedDesc())
        ),
        fetch = { options ->
            if (surveyId == null) {
                BackendApi.post(AdminRoutes.Survey.AllEntries()) {
                    withAuth()
                    setJsonBody(options)
                }
            } else {
                BackendApi.post(AdminRoutes.Survey.Entries(surveyId)) {
                    withAuth()
                    setJsonBody(options)
                }
            }
        },
        onViewEntry = { entry -> showEntryUpdateModal = entry },
    )

    showUpdateModal?.let { survey ->
        SurveyEditModal(survey) {
            showUpdateModal = null
            version++
        }
    }
    showEntryUpdateModal?.let { entry ->
        SurveyEntryEditModal(surveys, entry) {
            showEntryUpdateModal = null
            version++
        }
    }
}

@Composable
private fun SurveyEditModal(survey: Survey, onHide: () -> Unit) {
    Modal(onHide) {
        YaForm(
            initial = survey.copy(numAssignedEntries = null),
            onSubmitScope = { data ->
                val body = Survey(
                    id = survey.id,
                    status = survey.status,
                    title = data.string(Survey::title),
                    households = data.int(Survey::households),
                    notes = data.string(Survey::notes),
                    numAssignedEntries = null,
                    meta = null,
                )
                BackendApi.post(AdminRoutes.Survey.Id(survey.id)) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        Toasts.display(Toast.success("Erhebung gespeichert"))
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Speichern der Erhebung: ${error.message}"))
                        console.error("Fehler beim Speichern der Erhebung", error)
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Erhebung bearbeiten") }
            }
            YaFormTextInput(
                prop = Survey::id,
                label = { Text("ID") },
                type = InputType.Text,
                required = true,
                disabled = true,
            )
            YaFormDropdown(
                prop = Survey::status,
                label = { Text("Status") },
                options = SurveyStatusDisplayNames,
                required = true,
            )
            YaFormTextInput(
                prop = Survey::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormNumberInput(
                prop = Survey::households,
                label = { Text("Haushalte") },
                required = true,
                min = 1,
            )
            YaFormTextArea(
                prop = Survey::notes,
                label = { Text("Notizen") },
                required = false,
            )
            ModalFooterButtons(onHide, submitLabel = "Speichern")
        }
    }
}

@Composable
private fun SurveyEntryEditModal(
    surveys: List<PrefetchedEntry>,
    entry: SurveyEntry,
    onHide: () -> Unit,
) {
    Modal(onHide) {
        YaForm(
            initial = entry,
            onSubmitScope = { data ->
                val body = entry.copy(
                    surveyId = data.stringOrNull(SurveyEntry::surveyId)?.ifBlank { null },
                    customerId = data.stringOrNull(SurveyEntry::customerId)?.ifBlank { null },
                    notes = data.stringOrNull(SurveyEntry::notes)?.ifBlank { null },
                    notesExternal = data.stringOrNull(SurveyEntry::notesExternal)?.ifBlank { null },
                )
                BackendApi.post(AdminRoutes.Survey.UpdateEntry(entry.id)) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        Toasts.display(Toast.success("Eintrag gespeichert"))
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Speichern des Erhebungseintrags: ${error.message}"))
                        console.error("Fehler beim Speichern des Erhebungseintrags", error)
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Erhebungseintrag bearbeiten") }
            }
            YaFormDropdown(
                prop = SurveyEntry::surveyId,
                label = { Text("Erhebung") },
                options = surveys,
                required = false,
            )
//            YaFormDropdown(
//                prop = SurveyEntry::customerId,
//                label = { Text("Kunde") },
//                options = customers,
//                required = false,
//            )
            YaFormTextArea(
                prop = SurveyEntry::notes,
                label = { Text("Notizen (intern)") },
                required = false,
            )
            YaFormTextArea(
                prop = SurveyEntry::notesExternal,
                label = { Text("Notizen (extern)") },
                required = false,
            )
            ModalFooterButtons(onHide, submitLabel = "Speichern")
        }
    }
}
